import type { Store } from 'redux';
import type { Connect, TypedUseSelectorHook } from 'react-redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import logger from '../modules/logging/logger';
import rootReducer, { localStorePaths } from '../reducers';
import middlewares from '../middlewares';
import type { ThunkDispatcher } from '../actions/types';
import { configureStore } from './store';

export type RootState = ReturnType<typeof rootReducer>;
export type RootStore = Store<RootState>;
export type RootStateFn = () => RootState;

let store: RootStore;

export const getStore = (): RootStore => store;

export const useAppDispatch = (): ThunkDispatcher => useDispatch<ThunkDispatcher>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const connectApp: Connect<RootState> = connect;

export const createAppStore = (initialState?: Partial<RootState>, persistStore = true): RootStore => {
    store = configureStore({
        initialState,
        rootReducer,
        middlewares,
        localStorePaths: persistStore ? localStorePaths : undefined,
        onLoadError: (error) => {
            const state = getStore()?.getState();
            const { user } = state?.global ?? {};
            const { project } = state?.studio ?? {};
            setTimeout(() => {
                // at the time of loading app -> logger not yet created
                logger.error('Error parsing local storage value', {
                    userId: user?.id ?? null,
                    projectId: project?.id ?? null,
                    organizationId: project?.organizationId ?? null,
                    error: error
                });
            }, 1000);
        },
        stateSanitizer: (state: RootState) =>
            state?.studio?.frame?.element
                ? {
                      ...state,
                      studio: {
                          ...state.studio,
                          frame: {
                              ...state.studio.frame,
                              element: '<<TOO MUCH DATA TO SERIALIZE IN REDUX DEVTOOLS>>'
                          }
                      }
                  }
                : state
    });

    if (module.hot) {
        // Enable Webpack hot module replacement for reducers.
        module.hot.accept('../reducers', () => {
            store.replaceReducer(rootReducer);
        });
    }

    return store;
};
